import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { formatFeedbackText } from "../../utils/feedbackFormatter";
import nexticn from "../../Components/Assets/svg/chevron-right-solid.svg";
import backicn from "../../Components/Assets/svg/back-chevron-right-solid (1).svg";
// Platform icons
import x from "../../Components/Assets/Management/x-task.png";
import fb from "../../Components/Assets/Management/fb-task.png";
import li from "../../Components/Assets/Management/linkedin-task.png";
import insta from "../../Components/Assets/Management/instagram-task.png";
import mail from "../../Components/Assets/Management/email-icn.png";

// Helper function to format dates for display
const formatDateForDisplay = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toISOString().split("T")[0];
};

/**
 * Launched Task Section - displays and manages completed/posted tasks
 */
export function LaunchedTaskSection({
  companyProfile,
  launchedTaskWindow,
  showLaunchedTaskDetail,
  onLaunchedTaskWindowToggle,
  onLaunchedTaskDetailToggle
}) {
  const launchedTasksScrollRef = useRef(null);
  
  // Component state
  const [launchedTasks, setLaunchedTasks] = useState([]); // Posted tasks from API
  const [selectedLaunchedTasks, setSelectedLaunchedTasks] = useState([]); // Tasks selected for reports
  const [hasLaunchedTasks, setHasLaunchedTasks] = useState(false); // Tasks display flag

  // Utility functions
  const getStatusClass = (status) => {
    switch (status) {
      case "Approved":
        return { backgroundColor: "#5eb35eb7" };
      case "Pending":
        return { backgroundColor: "rgba(255, 145, 0, 0.69)" };
      case "Roll Back":
        return { backgroundColor: "rgba(245, 65, 0, 0.69)" };
      case "Posted":
        return { backgroundColor: "rgba(172, 5, 255, 0.76)" };
      default:
        return {};
    }
  };

  // Helper function to format recurrence pattern display
  const formatRecurrencePattern = (recurrence) => {
    if (!recurrence) return "";

    if (recurrence.recurrenceType === "monthly") {
      const week = recurrence.monthlyWeek || "first";
      const dayOfWeek = recurrence.monthlyDayOfWeek || "monday";

      const formattedDay =
        dayOfWeek.charAt(0).toUpperCase() + dayOfWeek.slice(1);
      const formattedWeek =
        week === "first"
          ? "1st"
          : week === "second"
          ? "2nd"
          : week === "third"
          ? "3rd"
          : week === "fourth"
          ? "4th"
          : "last";

      return `Every month on the ${formattedWeek} ${formattedDay}`;
    } else if (recurrence.recurrenceType === "weekly") {
      const dayOfWeek = recurrence.weeklyDayOfWeek || "monday";
      const formattedDay =
        dayOfWeek.charAt(0).toUpperCase() + dayOfWeek.slice(1);
      return `Every ${formattedDay}`;
    } else if (recurrence.recurrenceType === "bi-weekly") {
      const dayOfWeek = recurrence.weeklyDayOfWeek || "monday";
      const formattedDay =
        dayOfWeek.charAt(0).toUpperCase() + dayOfWeek.slice(1);
      return `Every other ${formattedDay}`;
    }

    return recurrence.recurrenceType || "";
  };

  // Data fetching functions
  const fetchLaunchedTasks = async () => {
    try {
      // Check if company profile is available before making API call
      if (!companyProfile?._id) return;

      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/task/tasks/${companyProfile._id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const launchedTasksData = response.data.filter(
        (task) => task.currentStatus === "Posted"
      );
      
      setLaunchedTasks(launchedTasksData);
      setHasLaunchedTasks(launchedTasksData.length > 0);
    } catch (error) {
      console.error("Error fetching launched tasks:", error);
      toast.error("Error loading launched tasks", {
        className: "gradient-toast",
        bodyClassName: "gradient-toast-body",
        progressClassName: "gradient-toast-progress",
      });
    }
  };

  // Effect to fetch launched tasks when component mounts or companyProfile changes
  useEffect(() => {
    if (companyProfile?._id) {
      fetchLaunchedTasks();
    }
  }, [companyProfile]);

  // Refetch on global task update events
  useEffect(() => {
    const onTasksUpdated = () => {
      if (companyProfile?._id) {
        fetchLaunchedTasks();
      }
    };
    window.addEventListener('hussme:tasks-updated', onTasksUpdated);
    return () => window.removeEventListener('hussme:tasks-updated', onTasksUpdated);
  }, [companyProfile?._id]);

  // Scroll functions for launched tasks horizontal navigation
  const scrollLaunchedTasksLeft = () => {
    if (launchedTasksScrollRef.current) {
      launchedTasksScrollRef.current.scrollBy({
        left: -350,
        behavior: "smooth",
      });
    }
  };

  const scrollLaunchedTasksRight = () => {
    if (launchedTasksScrollRef.current) {
      launchedTasksScrollRef.current.scrollBy({
        left: 350,
        behavior: "smooth",
      });
    }
  };

  // Task handlers
  const handlePreviewClick = (task) => {
    setSelectedLaunchedTasks(task);
    onLaunchedTaskDetailToggle();
  };

  return (
    <>
      {/* Launched Task Window */}
      {launchedTaskWindow && (
        <div className="launch-container">
          <div className="launched-page-title-exit-row">
            <div className="approve-title"> Posted Tasks🚀 </div>
            <div
              className="appproved-list-btn"
              onClick={onLaunchedTaskWindowToggle}
            >
              <img
                width="48"
                height="48"
                src="https://img.icons8.com/fluency/48/delete-sign.png"
                alt="delete-sign"
                className="status-cross-symbol"
              />
            </div>
          </div>

          <div className="task-arrows">
            <img
              src={backicn}
              alt=""
              className="assign-task-btn back"
              onClick={scrollLaunchedTasksLeft}
            />
            {hasLaunchedTasks ? (
              <div
                className="task-approved-row-container"
                ref={launchedTasksScrollRef}
              >
                {launchedTasks.map((task) => (
                  <div
                    className="yet-task-box-wrapper"
                    key={task._id}
                    // Remove onClick from here to avoid accidental window open
                  >
                    <div
                      className="yet-task-box"
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePreviewClick(task);
                      }}
                    >
                      <div className="inner-triangle-container">
                        <div className="yet-task-id">
                          <div className="yet-id">#{task.taskId}</div>
                        </div>
                        <button
                          className="yet-preview-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePreviewClick(task);
                          }}
                        >
                          Preview
                        </button>
                        <div className="yet-task-title">{task.title}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="temp-row-container yet">
                <div className="yet-temp-img"> </div>
                <div className="temp-title">
                  Task nearly complete? Let's check it off!
                </div>
              </div>
            )}

            <img
              src={nexticn}
              alt=""
              className="assign-task-btn next"
              onClick={scrollLaunchedTasksRight}
            />
          </div>
        </div>
      )}

      {/* Launched Task Detail View */}
      {showLaunchedTaskDetail && selectedLaunchedTasks && (
        <div className="yet-show-task-status-container">
          <div className=" close-button-row">
            <img
              onClick={onLaunchedTaskDetailToggle}
              width="48"
              height="48"
              src="https://img.icons8.com/fluency/48/delete-sign.png"
              alt="delete-sign"
              className="status-cross-symbol"
            />
          </div>
          <div className="status-top-floor">
            <div className="status-top-floor-row-1">
              <div className="status-col-1">
                <div className="status-row client-padding">
                  <div className="status-task-id">
                    <div className="task-id">Task ID</div>
                    <div className="task-id-number">
                      #{selectedLaunchedTasks?.taskId}
                    </div>
                  </div>
                  <div
                    className="task-current-status"
                    style={getStatusClass(selectedLaunchedTasks.currentStatus)}
                  >
                    {selectedLaunchedTasks.currentStatus}
                  </div>
                  <div className="status-task-title">
                    <div className="task-title-status">Task Title</div>
                    <div className="task-title-name-status task-h">
                      {selectedLaunchedTasks?.title}
                    </div>
                  </div>
                  {selectedLaunchedTasks.taskpurpose && (
                    <div className="status-task-title">
                      <div className="task-title-status">Task Objective</div>
                      <div className="task-title-name-status">
                        {selectedLaunchedTasks?.taskpurpose}
                      </div>
                    </div>
                  )}
                  {selectedLaunchedTasks.headline && (
                    <div className="status-task-title">
                      <div className="task-title-status">Main Headline</div>
                      <div className="task-title-name-status">
                        {selectedLaunchedTasks?.headline}
                      </div>
                    </div>
                  )}
                  {selectedLaunchedTasks.calltoaction && (
                    <div className="status-task-title">
                      <div className="task-title-status ">Tagline for Lead</div>
                      <div className="task-title-name-status">
                        {selectedLaunchedTasks?.calltoaction}
                      </div>
                    </div>
                  )}
                  {selectedLaunchedTasks.description && (
                    <div className="status-task-des">
                      <div className="task-title-status">Description</div>
                      <div className="task-title-name-status">
                        {selectedLaunchedTasks?.description}
                      </div>
                    </div>
                  )}
                  {/* Third row: Show recurrence details only if task is recurring */}
                  {selectedLaunchedTasks?.recurrence?.isRecurring && (
                    <div className="for-date-row ">
                      <div className="status-task-dates status-recurring-type">
                        <label className="task-title-status">
                          Recurrence Type
                        </label>
                        <div className="status-target-date recurring-type-div ">
                          <div className="task-title-name-status">
                            {formatRecurrencePattern(selectedLaunchedTasks.recurrence)}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  {(selectedLaunchedTasks.platform_x ||
                    selectedLaunchedTasks.platform_fb ||
                    selectedLaunchedTasks.platform_insta ||
                    selectedLaunchedTasks.platform_li ||
                    selectedLaunchedTasks.platform_via_mail) && (
                    <div className="platform-row-response">
                      <label className="task-title-status">
                        Selected Platforms has to Post:
                      </label>
                      <div className="social-checkbox">
                        {selectedLaunchedTasks.platform_x && (
                          <img src={x} className="checkbox__textwrapper" />
                        )}
                        {selectedLaunchedTasks.platform_fb && (
                          <img src={fb} className="checkbox__textwrapper" />
                        )}
                        {selectedLaunchedTasks.platform_insta && (
                          <img src={insta} className="checkbox__textwrapper" />
                        )}
                        {selectedLaunchedTasks.platform_li && (
                          <img src={li} className="checkbox__textwrapper" />
                        )}
                        {selectedLaunchedTasks.platform_via_mail && (
                          <img src={mail} className="checkbox__textwrapper" />
                        )}
                      </div>
                    </div>
                  )}{" "}
                  {/* First row: Always show Submission Date and Target Posting Date */}
                  <div className="for-date-row">
                    <div className="status-task-dates">
                      <label className="task-title-status">
                        Submission Date
                      </label>
                      <div className="status-deadline-date">
                        <input
                          type="date"
                          value={formatDateForDisplay(
                            selectedLaunchedTasks?.submissionForReview
                          )}
                          readOnly
                        />
                      </div>
                    </div>
                    <div className="status-task-dates">
                      <label className="task-title-status">
                        Target Posting Date
                      </label>
                      <div className="status-target-date">
                        <input
                          type="date"
                          name="targetPostingDate"
                          value={formatDateForDisplay(
                            selectedLaunchedTasks?.targetPostingDate
                          )}
                          readOnly
                        />
                      </div>
                    </div>
                  </div>
                  {/* Second row: Show recurrence end date only if task is recurring */}
                  {selectedLaunchedTasks?.recurrence?.isRecurring && (
                    <div className="for-date-row">
                      <div className="status-task-dates">
                        <label className="task-title-status">
                          Recurrence Start Date
                        </label>
                        <div className="status-deadline-date">
                          <input
                            type="date"
                            value={formatDateForDisplay(
                              selectedLaunchedTasks?.recurrence
                                ?.recurrenceStartDate
                            )}
                            readOnly
                          />
                        </div>
                      </div>
                      <div className="status-task-dates">
                        <label className="task-title-status">
                          Recurrence End Date
                        </label>
                        <div className="status-deadline-date">
                          <input
                            type="date"
                            value={formatDateForDisplay(
                              selectedLaunchedTasks?.recurrence
                                ?.recurrenceEndDate
                            )}
                            readOnly
                          />
                        </div>
                      </div>
                    </div>
                  )}
                  {selectedLaunchedTasks.adminPostComment && (
                    <div className="status-task-title">
                      <div className="task-title-status bright">
                        Admin Post Comments
                      </div>
                      <div className="task-title-name-status">
                        {selectedLaunchedTasks?.adminPostComment}
                      </div>
                    </div>
                  )}
                  {selectedLaunchedTasks.referenceImage && (
                    <div className="info-group-img">
                      <label className="task-title-status">
                        Reference Image
                      </label>
                      <a
                        href={selectedLaunchedTasks.referenceImage}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <img
                          src={selectedLaunchedTasks.referenceImage}
                          alt="Brand Relate Image 6"
                          className="profile-logo"
                        />
                      </a>
                    </div>
                  )}
                </div>
              </div>
              {selectedLaunchedTasks.rollbackFeedback && selectedLaunchedTasks.rollbackFeedback.length > 0 && (
                <div className="past-rollback">
                  <h3>Rollback Feedback</h3>
                  <ul>
                    {selectedLaunchedTasks.rollbackFeedback.map(
                      (feedback, index) => (
                        <li key={index}>
                          {feedback.clientSuggestImage ? (
                            <div className="info-group-img">
                              <a
                                href={feedback.clientSuggestImage}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <img
                                  src={feedback.clientSuggestImage}
                                  alt="Brand Relate Image 6"
                                  className="profile-logo"
                                />
                              </a>
                            </div>
                          ) : (
                            <div className="info-group-img">
                              <a
                                href={feedback.doneTaskImage}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <img
                                  src={feedback.doneTaskImage}
                                  alt="Brand Relate Image 6"
                                  className="profile-logo"
                                />
                              </a>
                            </div>
                          )}
                          {((feedback?.feedback ?? '').toString().trim().length > 0) && (
                            <div className="feed-index">
                              <div className="feedback-number">
                                {index + 1})
                              </div>
                              <div className="feedback-text">
                                {formatFeedbackText(feedback.feedback, `launched-feedback-${index}`)}
                              </div>
                            </div>
                          )}
                        </li>
                      )
                    )}
                  </ul>
                </div>
              )}
              <div className="status-col-2">
                <div className="status-title-preview">Task Preview</div>
                <div className="task-preview-img-container">
                  <img
                    src={selectedLaunchedTasks?.doneTaskImage}
                    className="yet-task-preview-img"
                    alt=""
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
